import {
    useCoursesForCartUpsellByIds,
    useFetchBundleFromCourse,
    type CourseForCartUpsell,
} from '@udemy/react-certification-preparation';
import {useExamByTopicId} from '@udemy/react-certification-vouchers';

export type BundleType = 'certification' | 'generic' | null;

export interface OtherCourseInBundle {
    course: CourseForCartUpsell | null;
    isLoading: boolean;
    error: Error | null;
}

export function useBundleForCourseUpsell(inCartCourseId: number) {
    const {bundle, isLoading: bundleLoading} = useFetchBundleFromCourse(inCartCourseId, {
        enabled: !!inCartCourseId,
    });
    const relatedTopicId = bundleLoading ? undefined : bundle?.topicId;
    const examInTopic = useExamByTopicId(relatedTopicId);

    const hasBundle = !bundleLoading && Boolean(bundle);
    const hasVoucherForTopic = Boolean(examInTopic.voucherPool ?? examInTopic.defaultPool);
    const examSettled = !relatedTopicId || !examInTopic.isLoading;

    const bundleType: BundleType =
        hasBundle && examSettled ? (hasVoucherForTopic ? 'certification' : 'generic') : null;

    const otherCourseEntry =
        hasBundle && bundle ? bundle.contents.find((c) => c.id !== inCartCourseId) : undefined;

    const idsToFetch: number[] = otherCourseEntry ? [otherCourseEntry.id] : [];

    const {
        courses,
        isLoading: coursesFetchLoading,
        error,
    } = useCoursesForCartUpsellByIds(idsToFetch);

    // `useCoursesForCartUpsellByIds([])` still reports isLoading:true — treat
    // no-other-course as settled so downstream gates don't hang.
    const otherCourseInBundle: OtherCourseInBundle = {
        course: courses[0] ?? null,
        isLoading:
            bundleLoading ||
            (idsToFetch.length > 0
                ? coursesFetchLoading
                : Boolean(relatedTopicId) && examInTopic.isLoading),
        error,
    };

    return {
        bundle,
        bundleLoading,
        bundleType,
        relatedTopicId,
        examInTopic,
        hasBundle: hasBundle && examSettled,
        hasVoucherForTopic,
        otherCourseInBundle,
    };
}
